import { useEffect, useState } from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import {
  Alert,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useLoadGoogleMaps } from '../hooks/useLoadGoogleMaps';
import { ZoneMap } from './ZoneMap';
import {
  BILL_ROUND_TRIP,
  distanceBeyondZone,
  estimateDrivingKm,
  LatLng,
  RATES,
  ZoneResult,
  testZone,
  transportCharge,
} from './zone';
import { Region } from './pricing.constants';

const money = (n: number) =>
  n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 2 });

/**
 * What a pickup costs.
 *
 * Inside Zone 1 the transfer is already inside the package price, so there is
 * nothing to add. Outside it, the base price plus the per-kilometre rate
 * measured from the zone border (decided 2026-08-26; rate raised 2026-08-27).
 */
export const TransportSection = ({ region }: { region: Region }) => {
  const { isLoaded, loadError } = useLoadGoogleMaps(
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  );

  const [selected, setSelected] = useState<any>(null);
  const [label, setLabel] = useState<string>('');
  const [point, setPoint] = useState<LatLng | null>(null);
  const [result, setResult] = useState<ZoneResult | null>(null);
  const [lookupFailed, setLookupFailed] = useState(false);
  /**
   * One-way driving distance from the zone border, and the road path it took.
   * `measured` distinguishes a routed number from the straight-line stand-in —
   * the two are labelled differently because only one is quotable.
   */
  const [drive, setDrive] = useState<
    {
      km: number;
      path: LatLng[];
      /** Where the billing starts — the route's exit, or the nearest edge if estimated. */
      from: LatLng;
      fromMunicipality: string;
      measured: boolean;
      note?: string;
    } | null
  >(null);

  const handleChange = (choice: any) => {
    setSelected(choice);
    setResult(null);
    setPoint(null);
    setDrive(null);
    setLookupFailed(false);

    const placeId = choice?.value?.place_id;
    if (!placeId || !window.google?.maps?.places) return;

    setLabel(choice?.label ?? '');
    const service = new window.google.maps.places.PlacesService(
      document.createElement('div'),
    );
    // `geometry` is the whole point here — the funeral form asks only for
    // address_components and name, which is why nothing in the system has ever
    // stored a latitude.
    service.getDetails(
      { placeId, fields: ['geometry', 'name', 'formatted_address'] },
      (place: any, status: any) => {
        const location = place?.geometry?.location;
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !location) {
          // Say so rather than showing a price derived from nothing.
          setLookupFailed(true);
          return;
        }
        const latLng = { lat: location.lat(), lng: location.lng() };
        setPoint(latLng);
        const zoneResult = testZone(latLng, region);
        setResult(zoneResult);

        if (zoneResult.inZone) return;

        // Measured along the route actually driven, from where it leaves the
        // zone — not from the nearest point on the polygon, which for somewhere
        // like Saint-Jean-sur-Richelieu sits across the water from any useful
        // road and sends the route doubling back on itself.
        void distanceBeyondZone(latLng, region)
          .then((beyond) => {
            setDrive({
              km: beyond.km,
              path: beyond.path,
              from: beyond.crossing,
              fromMunicipality: beyond.crossingMunicipality,
              measured: true,
            });
          })
          .catch((e: Error) => {
            // Fall back to the straight line from the nearest edge, clearly
            // labelled. A rough number the agent knows is rough beats none.
            setDrive({
              km: estimateDrivingKm(zoneResult.straightLineKm),
              path: [],
              from: zoneResult.borderPoint,
              fromMunicipality: zoneResult.borderMunicipality,
              measured: false,
              note: e.message,
            });
          });
      },
    );
  };

  // The same address is in one province's zone and not the other's, so a
  // result from before the toggle moved is simply wrong. Clear it rather than
  // leave a stale in/out answer on screen.
  useEffect(() => {
    setSelected(null);
    setLabel('');
    setPoint(null);
    setResult(null);
    setDrive(null);
    setLookupFailed(false);
  }, [region]);

  const charge = drive ? transportCharge(drive.km, region) : null;

  return (
    <Box sx={{ width: '100%' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Inside Zone 1 the transfer is already in the base price. Beyond it, $
          {RATES[region].perKm.toFixed(2)}/km {BILL_ROUND_TRIP ? 'round trip ' : ''}from the zone
          border.
        </Typography>

        <Box sx={{ maxWidth: 460, mb: 2 }}>
          {loadError ? (
            <Alert severity="error">
              Address search is unavailable — Google Maps failed to load, so the zone
              can't be checked here.
            </Alert>
          ) : isLoaded ? (
            <GooglePlacesAutocomplete
              selectProps={{
                value: selected,
                onChange: handleChange,
                placeholder: 'Where is the place of death?',
                isClearable: true,
                styles: { menu: (p: any) => ({ ...p, zIndex: 9999 }) },
              }}
              autocompletionRequest={{ componentRestrictions: { country: ['ca'] } }}
            />
          ) : (
            <TextField disabled fullWidth label="Loading address search…" />
          )}
        </Box>

        {lookupFailed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Couldn't get coordinates for that address, so the zone can't be checked.
            Quote it by hand rather than guessing.
          </Alert>
        )}

        {result?.inZone && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Chip label="Inside Zone 1" color="success" size="small" />
            <Typography variant="body2">
              {result.municipality} — transport is included. Nothing to add.
            </Typography>
          </Stack>
        )}

        {result && !result.inZone && !drive && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Outside Zone 1 — measuring the drive from the border…
          </Typography>
        )}

        {result && !result.inZone && drive && charge && (
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip label="Outside Zone 1" color="warning" size="small" />
              <Typography variant="body2">
                <strong>{money(charge.amount)}</strong> on top of the base price
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {charge.billableKm.toFixed(1)} km{BILL_ROUND_TRIP ? ' round trip' : ''} ×{' '}
              {money(RATES[region].perKm)} —{' '}
              {drive.measured
                ? `driving distance from where the route leaves the zone at ${drive.fromMunicipality}`
                : `estimated from the nearest zone edge at ${drive.fromMunicipality}`}
              .
            </Typography>

            {!drive.measured && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Couldn't get a driving distance ({drive.note}), so this is straight-line
                distance with a road factor applied — it runs about 10% out on average
                and up to 29% on the worst reference case. Confirm before quoting it.
              </Alert>
            )}

            {drive.measured && drive.note && (
              <Alert severity="warning" sx={{ mt: 1 }}>{drive.note}</Alert>
            )}

            {RATES[region].perKm !== RATES[region].quickbooks && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                This quotes {money(RATES[region].perKm)}/km, but the QuickBooks item still
                bills {money(RATES[region].quickbooks)}/km — a contract written today
                would charge{' '}
                <strong>{money(charge.billableKm * RATES[region].quickbooks)}</strong>{' '}
                instead. Reprice <em>Outside Zone Transportation</em> (item 1010000013)
                before quoting the new rate.
              </Alert>
            )}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {region === 'ontario'
            ? 'Zone 1 — the eleven municipalities we cover, plus Milton east of the zone line. Transport inside this area is included.'
            : 'Zone 1 — the island of Montreal, Laval, and the south shore out to Autoroute 30. Transport inside this area is included.'}
        </Typography>
        <ZoneMap
          ready={isLoaded}
          region={region}
          routePath={drive?.path}
          borderPoint={drive?.from ?? (result && !result.inZone ? result.borderPoint : null)}
          destination={point}
        />
        {label && (
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        )}
    </Box>
  );
};

export default TransportSection;
